import { useEffect, useMemo, useState } from 'react';
import { sheetUrl } from './config';
import { useTheme } from './theme';
import { useDebug } from './debug';
import users from './users';

const normalizeAudience = (a) => (a || '').toString().trim().toLowerCase();
const audienceLabel = (a) => {
  const x = normalizeAudience(a);
  if (x === 'board' || x === 'board only' || x === 'board-only') return 'Board Only';
  if (x === 'members' || x === 'members only' || x === 'members-only') return 'Members Only';
  return 'Everyone';
};

const stylesFor = (theme) => {
  const dark = theme === 'dark';

  return {
    page: {
      padding: 16,
      fontFamily: 'Arial',
      background: dark ? '#0f172a' : '#f8fafc',
      color: dark ? '#e5e7eb' : '#0f172a',
      minHeight: '100vh',
    },
    headerRow: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-end',
      gap: 12,
      flexWrap: 'wrap',
      marginBottom: 10,
      maxWidth: 1100,
    },
    kicker: {
      fontSize: 12,
      color: dark ? '#94a3b8' : '#64748b',
      letterSpacing: 1,
      textTransform: 'uppercase',
    },
    h2: { margin: 0, fontSize: 24, color: dark ? '#f8fafc' : '#0f172a' },
    headerActions: { display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' },
    btn: {
      padding: '8px 12px',
      borderRadius: 10,
      border: `1px solid ${dark ? '#334155' : '#cbd5e1'}`,
      background: dark ? '#111827' : '#ffffff',
      color: dark ? '#e5e7eb' : '#0f172a',
      cursor: 'pointer',
      fontSize: 14,
    },
    btnActive: {
      padding: '8px 12px',
      borderRadius: 10,
      border: `1px solid ${dark ? '#0ea5e9' : '#0284c7'}`,
      background: dark ? '#0ea5e9' : '#0284c7',
      color: 'white',
      cursor: 'pointer',
      fontSize: 14,
      fontWeight: 800,
    },
    card: {
      border: `1px solid ${dark ? '#1f2937' : '#e5e7eb'}`,
      borderRadius: 14,
      padding: 14,
      marginBottom: 12,
      background: dark ? 'rgba(17, 24, 39, 0.7)' : '#ffffff',
      boxShadow: dark
        ? '0 10px 24px rgba(0,0,0,0.25)'
        : '0 10px 24px rgba(15, 23, 42, 0.08)',
      maxWidth: 1100,
    },
    label: {
      fontSize: 12,
      color: dark ? '#cbd5e1' : '#475569',
      fontWeight: 700,
      marginBottom: 6,
    },
    select: {
      width: '100%',
      padding: '10px 12px',
      borderRadius: 12,
      border: `1px solid ${dark ? '#334155' : '#cbd5e1'}`,
      background: dark ? '#0b1220' : '#ffffff',
      color: dark ? '#e5e7eb' : '#0f172a',
      fontSize: 14,
      boxSizing: 'border-box',
      maxWidth: 520,
    },
    grid2: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: 10,
      marginTop: 10,
    },
    tileYes: {
      borderRadius: 16,
      padding: 16,
      border: `1px solid ${dark ? 'rgba(34,197,94,0.45)' : '#16a34a'}`,
      background: dark ? 'rgba(34,197,94,0.08)' : 'rgba(34,197,94,0.06)',
    },
    tileNo: {
      borderRadius: 16,
      padding: 16,
      border: `1px solid ${dark ? 'rgba(239,68,68,0.45)' : '#dc2626'}`,
      background: dark ? 'rgba(239,68,68,0.08)' : 'rgba(239,68,68,0.06)',
    },
    tileLabel: {
      fontSize: 12,
      color: dark ? '#94a3b8' : '#64748b',
      letterSpacing: 1,
      textTransform: 'uppercase',
      fontWeight: 800,
    },
    tileNumber: { fontSize: 44, fontWeight: 900, marginTop: 6, lineHeight: 1 },
    muted: { fontSize: 12, color: dark ? '#94a3b8' : '#64748b', marginTop: 6 },
    auditTable: {
      width: '100%',
      borderCollapse: 'collapse',
      minWidth: 900,
      fontSize: 12,
    },
    th: {
      textAlign: 'left',
      padding: 8,
      borderBottom: `1px solid ${dark ? '#1f2937' : '#e5e7eb'}`,
      whiteSpace: 'nowrap',
    },
    td: {
      padding: 8,
      borderBottom: `1px solid ${dark ? '#111827' : '#f1f5f9'}`,
      verticalAlign: 'top',
    },
    pill: {
      display: 'inline-block',
      padding: '2px 8px',
      borderRadius: 999,
      fontSize: 11,
      fontWeight: 800,
    },
    pillYes: { background: 'rgba(34,197,94,0.15)', color: '#16a34a' },
    pillNo: { background: 'rgba(239,68,68,0.15)', color: '#dc2626' },
  };
};

export default function VoteTally() {
  const [theme, setTheme] = useTheme();
  const [debug] = useDebug();
  const S = stylesFor(theme);

  const [ballots, setBallots] = useState([]);
  const [selectedType, setSelectedType] = useState('');
  const [rows, setRows] = useState([]);
  const [memberRoles, setMemberRoles] = useState({}); // { '92': 'board', ... }
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');
  const [view, setView] = useState('summary'); // 'summary' | 'participation'

  const fetchAll = async () => {
    setLoading(true);
    setStatus('');
    try {
      const bRes = await fetch(sheetUrl('Ballots'), { cache: 'no-store' });
      const bData = await bRes.json();
      const bList = (bData || [])
        .filter((r) => (r.name || '').toString().trim() !== '')
        .map((r) => ({
          name: (r.name || '').toString().trim(),
          display: (r.display || '').toString().trim(),
          audience: audienceLabel(r.audience || ''),
          maxVotes: r.maxVotes ? Number(r.maxVotes) : 2,
        }))
        .sort((a, b) => a.name.localeCompare(b.name));
      setBallots(bList);

      const vRes = await fetch(sheetUrl('Votes'), { cache: 'no-store' });
      const vData = await vRes.json();
      setRows(vData || []);

      // Members.role lookup — needed to determine eligibility on board-only ballots
      try {
        const mRes = await fetch(sheetUrl('Members'), { cache: 'no-store' });
        if (mRes.ok) {
          const mData = await mRes.json();
          const map = {};
          (mData || []).forEach((m) => {
            const num = String(m.memberNumber || m['Member Number'] || '').trim();
            if (num) map[num] = String(m.role || '').toLowerCase().trim();
          });
          setMemberRoles(map);
        }
      } catch (e) {
        console.warn('VoteTally: Members fetch failed', e);
      }

      if (!selectedType && bList.length) setSelectedType(bList[0].name);
    } catch (e) {
      console.error('VoteTally: fetch failed', e);
      setRows([]);
      setBallots([]);
      setStatus('Failed to load votes/ballots.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const grouped = useMemo(() => {
    const map = {};
    (rows || []).forEach((r) => {
      const type = (r.type || '').toString().trim();
      if (!type) return;
      if (!map[type]) map[type] = { yes: 0, no: 0, total: 0 };
      const c = (r.choice || '').toLowerCase();
      if (c === 'yes') map[type].yes += 1;
      if (c === 'no') map[type].no += 1;
      map[type].total += 1;
    });
    return map;
  }, [rows]);

  const selectedBallot = useMemo(
    () => ballots.find((b) => b.name === selectedType) || null,
    [ballots, selectedType]
  );

  const totals = useMemo(() => {
    const init = { yes: 0, no: 0, total: 0 };
    (rows || []).forEach((r) => {
      if ((r.type || '') !== selectedType) return;
      const c = (r.choice || '').toLowerCase();
      if (c === 'yes') init.yes += 1;
      if (c === 'no') init.no += 1;
      init.total += 1;
    });
    return init;
  }, [rows, selectedType]);

  const auditRows = useMemo(() => {
    return (rows || [])
      .filter((r) => (r.type || '') === selectedType)
      .slice()
      .sort((a, b) => (a.timestamp || '') < (b.timestamp || '') ? 1 : -1);
  }, [rows, selectedType]);

  // Eligible voters for this ballot, based on audience.
  // Excludes role-only logins (chef, kitchen, admin, etc.) — they aren't voters.
  const eligibleMembers = useMemo(() => {
    if (!selectedBallot) return [];
    let pool = Object.values(users).filter(
      (u) =>
        !!u.memberNumber &&
        !u.isChef &&
        !u.isKitchen &&
        !u.isAdmin &&
        !u.isAccountant &&
        !u.isKiosk
    );
    if (selectedBallot.audience === 'Board Only') {
      pool = pool.filter(
        (u) => memberRoles[String(u.memberNumber)] === 'board'
      );
    }
    return pool;
  }, [selectedBallot, memberRoles]);

  // Who voted: dedupe by memberNumber, keep newest timestamp.
  // Note: we deliberately don't include their `choice` here — that info lives
  // only in the Votes sheet for direct review.
  const voted = useMemo(() => {
    const map = {};
    auditRows.forEach((r) => {
      const num = String(r.memberNumber || '').trim();
      if (!num) return;
      const ts = r.timestamp || r.voteDate || '';
      if (!map[num] || ts > map[num].timestamp) {
        map[num] = {
          memberNumber: num,
          name: r.memberName || '',
          timestamp: ts,
        };
      }
    });
    return Object.values(map).sort((a, b) =>
      (b.timestamp || '').localeCompare(a.timestamp || '')
    );
  }, [auditRows]);

  const votedSet = useMemo(
    () => new Set(voted.map((v) => v.memberNumber)),
    [voted]
  );

  const notVoted = useMemo(() => {
    return eligibleMembers
      .filter((u) => !votedSet.has(String(u.memberNumber)))
      .map((u) => ({
        memberNumber: String(u.memberNumber),
        name: u.name || '',
      }))
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [eligibleMembers, votedSet]);

  // Anomaly detection (same member from multiple devices, etc.) is kept
  // available under the debug toggle but never reveals the choice.
  const auditFlags = useMemo(() => {
    const byMember = {};
    const byDevice = {};
    auditRows.forEach((r) => {
      const m = String(r.memberNumber || '').trim();
      const d = String(r.deviceId || '').trim();
      if (m) {
        byMember[m] = byMember[m] || new Set();
        if (d) byMember[m].add(d);
      }
      if (d) {
        byDevice[d] = byDevice[d] || new Set();
        if (m) byDevice[d].add(m);
      }
    });
    return { byMember, byDevice };
  }, [auditRows]);

  const anomalies = useMemo(() => {
    const out = [];
    Object.entries(auditFlags.byMember).forEach(([m, devs]) => {
      if (devs.size > 1) out.push(`Member #${m} voted from ${devs.size} different devices`);
    });
    Object.entries(auditFlags.byDevice).forEach(([d, mems]) => {
      if (mems.size > 1) out.push(`Device ${d.slice(0, 10)}… cast votes for ${mems.size} different members`);
    });
    return out;
  }, [auditFlags]);

  const typeOptions = useMemo(() => {
    const fromBallots = ballots.map((b) => b.name);
    const fromVotes = Object.keys(grouped);
    const all = Array.from(new Set([...fromBallots, ...fromVotes]));
    all.sort((a, b) => a.localeCompare(b));
    return all;
  }, [ballots, grouped]);

  return (
    <div style={S.page}>
      <div style={S.headerRow}>
        <div>
          <div style={S.kicker}>Admin</div>
          <h2 style={S.h2}>🗳️ Vote Tally</h2>
          <div style={S.muted}>
            Pick a ballot type to see totals or individual records.
          </div>
        </div>

        <div style={S.headerActions}>
          <button
            style={view === 'summary' ? S.btnActive : S.btn}
            onClick={() => setView('summary')}
          >
            Summary
          </button>
          <button
            style={view === 'participation' ? S.btnActive : S.btn}
            onClick={() => setView('participation')}
          >
            Participation
          </button>
          <button style={S.btn} onClick={() => setTheme((t) => (t === 'dark' ? 'light' : 'dark'))}>
            {theme === 'dark' ? '🌙 Dark' : '☀️ Light'}
          </button>
          <button style={S.btn} onClick={fetchAll}>
            Refresh
          </button>
        </div>
      </div>

      <div style={S.card}>
        <div style={S.label}>Ballot (type)</div>
        <select
          value={selectedType}
          onChange={(e) => setSelectedType(e.target.value)}
          style={S.select}
          disabled={loading || typeOptions.length === 0}
        >
          {typeOptions.length === 0 ? (
            <option value="">No ballots found</option>
          ) : (
            typeOptions.map((t) => {
              const meta = ballots.find((b) => b.name === t);
              const label = meta?.display ? `${t} — ${meta.display}` : t;
              return (
                <option key={t} value={t}>
                  {label}
                </option>
              );
            })
          )}
        </select>

        {selectedBallot && (
          <div style={S.muted}>
            Audience: <b>{selectedBallot.audience}</b> • Max votes:{' '}
            <b>{selectedBallot.maxVotes}</b>
          </div>
        )}

        {loading ? (
          <div style={{ marginTop: 10 }}>Loading…</div>
        ) : view === 'summary' ? (
          <>
            <div style={S.grid2}>
              <div style={S.tileYes}>
                <div style={S.tileLabel}>Yes</div>
                <div style={S.tileNumber}>{totals.yes}</div>
              </div>
              <div style={S.tileNo}>
                <div style={S.tileLabel}>No</div>
                <div style={S.tileNumber}>{totals.no}</div>
              </div>
            </div>

            <div style={S.muted}>
              Total votes cast: <b>{totals.total}</b>
              {status ? ` • ${status}` : ''}
            </div>

            <hr
              style={{
                border: 0,
                borderTop: `1px solid ${theme === 'dark' ? '#1f2937' : '#e5e7eb'}`,
                margin: '14px 0',
              }}
            />

            <div style={S.label}>All ballots (overview)</div>
            <div style={{ fontSize: 13, overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 520 }}>
                <thead>
                  <tr>
                    <th style={S.th}>Type</th>
                    <th style={{ ...S.th, textAlign: 'right' }}>Yes</th>
                    <th style={{ ...S.th, textAlign: 'right' }}>No</th>
                    <th style={{ ...S.th, textAlign: 'right' }}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {typeOptions.map((t) => (
                    <tr key={t}>
                      <td style={S.td}>
                        <button
                          onClick={() => setSelectedType(t)}
                          style={{
                            background: 'transparent',
                            border: 'none',
                            color: theme === 'dark' ? '#93c5fd' : '#2563eb',
                            padding: 0,
                            cursor: 'pointer',
                            textAlign: 'left',
                            fontWeight: selectedType === t ? 900 : 700,
                          }}
                        >
                          {t}
                        </button>
                        {ballots.find((b) => b.name === t)?.display ? (
                          <div style={S.muted}>
                            {ballots.find((b) => b.name === t).display}
                          </div>
                        ) : null}
                      </td>
                      <td style={{ ...S.td, textAlign: 'right' }}>
                        {grouped[t]?.yes || 0}
                      </td>
                      <td style={{ ...S.td, textAlign: 'right' }}>
                        {grouped[t]?.no || 0}
                      </td>
                      <td style={{ ...S.td, textAlign: 'right' }}>
                        {grouped[t]?.total || 0}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          // PARTICIPATION VIEW — who voted, who hasn't. Choices are NOT
          // displayed here; they live only in the Votes sheet for direct
          // review.
          <>
            <div style={{ marginTop: 6, fontSize: 13 }}>
              Participation for <b>{selectedType || '(none)'}</b>
              {selectedBallot ? (
                <> — eligible voters: <b>{eligibleMembers.length}</b> · voted: <b>{voted.length}</b> · turnout:{' '}
                <b>{eligibleMembers.length ? Math.round((voted.length / eligibleMembers.length) * 100) : 0}%</b></>
              ) : null}
            </div>
            <div style={{ marginTop: 4, ...S.muted }}>
              How each individual voted is intentionally not shown here. To
              review choices, open the <b>Votes</b> sheet directly.
            </div>

            {anomalies.length > 0 && debug && (
              <div
                style={{
                  marginTop: 10,
                  padding: 10,
                  borderRadius: 10,
                  border: '1px solid #b45309',
                  background: 'rgba(180, 83, 9, 0.08)',
                  fontSize: 12,
                }}
              >
                <div style={{ fontWeight: 800, marginBottom: 4 }}>⚠ Anomalies detected</div>
                {anomalies.map((a, i) => (
                  <div key={i}>{a}</div>
                ))}
              </div>
            )}

            <div
              style={{
                marginTop: 12,
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                gap: 12,
              }}
            >
              {/* Voted */}
              <div
                style={{
                  border: `1px solid ${theme === 'dark' ? '#1f2937' : '#e5e7eb'}`,
                  borderRadius: 12,
                  padding: 12,
                  background: theme === 'dark' ? 'rgba(34,197,94,0.06)' : 'rgba(34,197,94,0.04)',
                }}
              >
                <div style={{ fontWeight: 800, marginBottom: 6 }}>
                  ✅ Voted ({voted.length})
                </div>
                {voted.length === 0 ? (
                  <div style={S.muted}>Nobody has voted yet.</div>
                ) : (
                  <div style={{ maxHeight: 380, overflowY: 'auto' }}>
                    {voted.map((v) => (
                      <div
                        key={v.memberNumber}
                        style={{
                          padding: '6px 0',
                          borderBottom: `1px dashed ${theme === 'dark' ? '#1f2937' : '#e5e7eb'}`,
                          fontSize: 13,
                        }}
                      >
                        <div style={{ fontWeight: 700 }}>
                          {v.name || '(unknown)'}{' '}
                          <span style={{ ...S.muted, fontWeight: 400 }}>#{v.memberNumber}</span>
                        </div>
                        <div style={S.muted}>{v.timestamp || '—'}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Hasn't voted */}
              <div
                style={{
                  border: `1px solid ${theme === 'dark' ? '#1f2937' : '#e5e7eb'}`,
                  borderRadius: 12,
                  padding: 12,
                  background: theme === 'dark' ? 'rgba(148,163,184,0.06)' : 'rgba(148,163,184,0.04)',
                }}
              >
                <div style={{ fontWeight: 800, marginBottom: 6 }}>
                  ⏳ Hasn't voted ({notVoted.length})
                </div>
                {notVoted.length === 0 ? (
                  <div style={S.muted}>
                    {eligibleMembers.length === 0
                      ? 'No eligible voters found for this ballot.'
                      : 'Everyone eligible has voted.'}
                  </div>
                ) : (
                  <div style={{ maxHeight: 380, overflowY: 'auto' }}>
                    {notVoted.map((n) => (
                      <div
                        key={n.memberNumber}
                        style={{
                          padding: '6px 0',
                          borderBottom: `1px dashed ${theme === 'dark' ? '#1f2937' : '#e5e7eb'}`,
                          fontSize: 13,
                        }}
                      >
                        <div style={{ fontWeight: 700 }}>
                          {n.name || '(unknown)'}{' '}
                          <span style={{ ...S.muted, fontWeight: 400 }}>#{n.memberNumber}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
